/**
 * Google Tasks Sync Engine - Phase 4 Architecture
 *
 * Encapsulates bidirectional sync and conversion logic between Google Tasks and FlowState.
 * Extracted from GoogleTasksSyncService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type { SyncResult, GoogleTask, GoogleTaskSyncMapping, CreateGoogleTaskRequest, UpdateGoogleTaskRequest } from "@/types/google-tasks";
import type { Todo } from "@flowstate/types";
import {
  GoogleTasksScopeError,
  resolveExportListId,
  sortGoogleTasksParentsFirst,
  todoHasExportableScope,
} from "./google-tasks-scope";
import { GoogleTasksApiClient } from "./google-tasks-api-client.service";
import { GoogleTasksListService } from "./google-tasks-list.service";
import { GoogleTasksTaskService } from "./google-tasks-task.service";

const log = logger.createNamespace("GoogleTasksSyncEngine");

export interface IGoogleTasksSyncEngine extends IService {
  syncFromGoogle(userId: string, listId?: string): Promise<ServiceResponse<SyncResult>>;
  syncToGoogle(userId: string, todoId?: string): Promise<ServiceResponse<SyncResult>>;
  syncBidirectional(userId: string): Promise<ServiceResponse<SyncResult>>;
  syncFlowStateTodoToGoogle(userId: string, todo: Todo): Promise<void>;
  syncGoogleTaskToFlowState(userId: string, listId: string, googleTask: GoogleTask): Promise<void>;
  createFlowStateTodoFromGoogle(userId: string, listId: string, googleTask: GoogleTask): Promise<void>;
  updateFlowStateTodoFromGoogle(todoId: string, listId: string, googleTask: GoogleTask): Promise<void>;
  createGoogleTaskFromFlowState(listId: string, todo: Todo): Promise<GoogleTask>;
  updateGoogleTaskFromFlowState(taskId: string, todo: Todo): Promise<GoogleTask>;
  getTodosNeedingSync(userId: string, specificTodoId?: string): Promise<Todo[]>;
}

export class GoogleTasksSyncEngine implements IGoogleTasksSyncEngine {
  public readonly name = "GoogleTasksSyncEngine";
  private readonly supabase = typedSupabase;
  private apiClient: GoogleTasksApiClient;
  private listService: GoogleTasksListService;
  private taskService: GoogleTasksTaskService;

  constructor(
    apiClient: GoogleTasksApiClient,
    listService: GoogleTasksListService,
    taskService: GoogleTasksTaskService
  ) {
    this.apiClient = apiClient;
    this.listService = listService;
    this.taskService = taskService;
  }

  async initialize(): Promise<void> {
    log.info("Google Tasks Sync Engine initialized");
  }

  async dispose(): Promise<void> {
    log.info("Google Tasks Sync Engine disposed");
  }

  async syncFromGoogle(
    userId: string,
    listId?: string
  ): Promise<ServiceResponse<SyncResult>> {
    log.info("Starting sync from Google", { userId, listId });
    const startTime = Date.now();

    const { data: syncLog, error: logError } = await this.supabase
      .from("google_task_sync_logs")
      .insert({
        user_id: userId,
        operation: "incremental_sync",
        direction: "google_to_flowstate",
        status: "started",
        trigger_type: "manual",
      })
      .select()
      .single();

    if (logError || !syncLog) {
      return createErrorResponse("Failed to create sync log");
    }

    try {
      const listsToSync = listId
        ? [listId]
        : await this.listService.getEnabledListIds(userId);

      let tasksSynced = 0;
      let tasksFailed = 0;
      const conflictsDetected = 0;
      const resultErrors: Array<{ taskId?: string; error: string }> = [];
      const syncLogErrors: Array<{
        task_id?: string;
        error: string;
        timestamp: string;
      }> = [];

      for (const currentListId of listsToSync) {
        const tasksResult = await this.taskService.getTasks(userId, currentListId);
        if (!tasksResult.success || !tasksResult.data) {
          tasksFailed++;
          resultErrors.push({
            taskId: currentListId,
            error: `Failed to get tasks for list ${currentListId}`,
          });
          syncLogErrors.push({
            task_id: currentListId,
            error: `Failed to get tasks for list ${currentListId}`,
            timestamp: new Date().toISOString(),
          });
          continue;
        }

        const orderedTasks = sortGoogleTasksParentsFirst(tasksResult.data);
        for (const googleTask of orderedTasks) {
          try {
            await this.syncGoogleTaskToFlowState(userId, currentListId, googleTask);
            tasksSynced++;
          } catch (error) {
            tasksFailed++;
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            resultErrors.push({
              taskId: googleTask.id,
              error: errorMessage,
            });
            syncLogErrors.push({
              task_id: googleTask.id,
              error: errorMessage,
              timestamp: new Date().toISOString(),
            });
          }
        }
      }

      const duration = Date.now() - startTime;
      await this.supabase
        .from("google_task_sync_logs")
        .update({
          status: tasksFailed > 0 ? "partial_success" : "success",
          tasks_synced: tasksSynced,
          tasks_failed: tasksFailed,
          conflicts_detected: conflictsDetected,
          errors: syncLogErrors,
          completed_at: new Date().toISOString(),
          duration_ms: duration,
        })
        .eq("id", syncLog.id);

      log.success("Sync from Google completed", { tasksSynced, tasksFailed, duration });

      await this.apiClient.logGoogleTasksActivity(
        userId,
        listId,
        syncLog.id,
        "sync_completed",
        "gtasks.sync_completed",
        {
          tasks_synced: tasksSynced,
          tasks_failed: tasksFailed,
          conflicts_detected: conflictsDetected,
          sync_direction: "google_to_flowstate",
          sync_log_id: syncLog.id,
        }
      );

      return createSuccessResponse({
        success: tasksFailed === 0,
        syncLogId: syncLog.id,
        tasksSynced,
        tasksFailed,
        conflictsDetected,
        errors: resultErrors,
        duration_ms: duration,
      });
    } catch (error) {
      log.error("Sync from Google failed", { error });

      await this.supabase
        .from("google_task_sync_logs")
        .update({
          status: "failed",
          completed_at: new Date().toISOString(),
          errors: [
            {
              error: error instanceof Error ? error.message : "Unknown error",
              timestamp: new Date().toISOString(),
            },
          ],
        })
        .eq("id", syncLog.id);

      return createErrorResponse(
        error instanceof Error ? error.message : "Sync failed"
      );
    }
  }

  async syncToGoogle(
    userId: string,
    todoId?: string
  ): Promise<ServiceResponse<SyncResult>> {
    log.info("Starting sync to Google", { userId, todoId });
    const startTime = Date.now();

    const { data: syncLog, error: logError } = await this.supabase
      .from("google_task_sync_logs")
      .insert({
        user_id: userId,
        operation: todoId ? "task_update" : "incremental_sync",
        direction: "flowstate_to_google",
        status: "started",
        trigger_type: "manual",
      })
      .select()
      .single();

    if (logError || !syncLog) {
      return createErrorResponse("Failed to create sync log");
    }

    try {
      const todosToSync = await this.getTodosNeedingSync(userId, todoId);
      log.info(`Found ${todosToSync.length} todos to sync to Google`, { userId });

      let tasksSynced = 0;
      let tasksFailed = 0;
      const resultErrors: Array<{ taskId?: string; error: string }> = [];
      const syncLogErrors: Array<{
        task_id?: string;
        error: string;
        timestamp: string;
      }> = [];

      for (const todo of todosToSync) {
        try {
          await this.syncFlowStateTodoToGoogle(userId, todo);
          tasksSynced++;
        } catch (error) {
          log.error("Failed to sync todo to Google", { todoId: todo.id, error });
          tasksFailed++;
          const errorMessage = `Failed to sync todo ${todo.id}: ${
            error instanceof Error ? error.message : "Unknown error"
          }`;
          resultErrors.push({ taskId: todo.id, error: errorMessage });
          syncLogErrors.push({
            task_id: todo.id,
            error: errorMessage,
            timestamp: new Date().toISOString(),
          });
        }
      }

      await this.supabase
        .from("google_task_sync_logs")
        .update({
          status: tasksFailed === 0 ? "success" : "partial_success",
          tasks_synced: tasksSynced,
          tasks_failed: tasksFailed,
          conflicts_detected: 0,
          errors: syncLogErrors,
          completed_at: new Date().toISOString(),
          duration_ms: Date.now() - startTime,
        })
        .eq("id", syncLog.id);

      const duration = Date.now() - startTime;
      return createSuccessResponse({
        success: true,
        syncLogId: syncLog.id,
        tasksSynced,
        tasksFailed,
        conflictsDetected: 0,
        errors: resultErrors,
        duration_ms: duration,
      });
    } catch (error) {
      log.error("Sync to Google failed", { error });
      return createErrorResponse(
        error instanceof Error ? error.message : "Sync failed"
      );
    }
  }

  async syncBidirectional(userId: string): Promise<ServiceResponse<SyncResult>> {
    log.info("Starting bidirectional sync", { userId });

    const fromGoogleResult = await this.syncFromGoogle(userId);
    if (!fromGoogleResult.success) {
      return fromGoogleResult;
    }

    const toGoogleResult = await this.syncToGoogle(userId);
    if (!toGoogleResult.success) {
      return toGoogleResult;
    }

    const combinedResult: SyncResult = {
      success: true,
      syncLogId: `${fromGoogleResult.data!.syncLogId},${toGoogleResult.data!.syncLogId}`,
      tasksSynced:
        fromGoogleResult.data!.tasksSynced + toGoogleResult.data!.tasksSynced,
      tasksFailed:
        fromGoogleResult.data!.tasksFailed + toGoogleResult.data!.tasksFailed,
      conflictsDetected:
        fromGoogleResult.data!.conflictsDetected +
        toGoogleResult.data!.conflictsDetected,
      errors: [...fromGoogleResult.data!.errors, ...toGoogleResult.data!.errors],
      duration_ms:
        fromGoogleResult.data!.duration_ms + toGoogleResult.data!.duration_ms,
    };

    return createSuccessResponse(combinedResult);
  }

  async getTodosNeedingSync(userId: string, specificTodoId?: string): Promise<Todo[]> {
    const bindings = await this.listService.getEnabledListBindings(userId);

    if (specificTodoId) {
      const { data, error } = await this.supabase
        .from("todos")
        .select("*")
        .eq("id", specificTodoId)
        .single();

      if (error || !data) {
        log.error("Failed to get specific todo for sync", { error, todoId: specificTodoId });
        return [];
      }
      const todo = data as Todo;
      if (!todoHasExportableScope(todo, bindings)) {
        return [];
      }
      return [todo];
    }

    const { data: allTodos, error: todosError } = await this.supabase
      .from("todos")
      .select("*")
      .eq("created_by", userId)
      .not("status", "in", "(\"cancelled\")");

    if (todosError || !allTodos) {
      log.error("Failed to get todos needing sync", { error: todosError });
      return [];
    }

    const { data: mappings } = await this.supabase
      .from("google_task_sync_mappings")
      .select("flowstate_todo_id, last_synced_at")
      .eq("user_id", userId);

    const mappingsByTodoId = new Map(
      (mappings ?? []).map((m) => [m.flowstate_todo_id, m.last_synced_at])
    );

    return (allTodos as Todo[]).filter((todo) => {
      if (!todoHasExportableScope(todo, bindings)) {
        return false;
      }
      const lastSynced = mappingsByTodoId.get(todo.id);
      if (!lastSynced) return true;
      return new Date(todo.updated_at) > new Date(lastSynced);
    });
  }

  async syncFlowStateTodoToGoogle(userId: string, todo: Todo): Promise<void> {
    const bindings = await this.listService.getEnabledListBindings(userId);

    const { data: existingMapping } = await this.supabase
      .from("google_task_sync_mappings")
      .select("*")
      .eq("flowstate_todo_id", todo.id)
      .maybeSingle();

    let listId: string;
    try {
      listId = resolveExportListId(todo, bindings, existingMapping?.google_task_list_id);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "No list configured for scope";
      throw new GoogleTasksScopeError(message);
    }

    if (existingMapping) {
      const updatedTask = await this.updateGoogleTaskFromFlowState(
        existingMapping.google_task_id,
        todo
      );

      await this.supabase
        .from("google_task_sync_mappings")
        .update({
          google_task_list_id: listId,
          last_synced_at: new Date().toISOString(),
          flowstate_last_modified: todo.updated_at,
          google_last_modified: updatedTask.updated,
          google_etag: updatedTask.etag,
          google_position: updatedTask.position,
          sync_status: "synced",
        })
        .eq("flowstate_todo_id", todo.id);
    } else {
      const googleTask = await this.createGoogleTaskFromFlowState(listId, todo);

      await this.supabase
        .from("google_task_sync_mappings")
        .insert({
          flowstate_todo_id: todo.id,
          google_task_id: googleTask.id,
          google_task_list_id: listId,
          user_id: userId,
          sync_direction: "bidir",
          sync_status: "synced",
          last_synced_at: new Date().toISOString(),
          flowstate_last_modified: todo.updated_at,
          google_last_modified: googleTask.updated,
          google_etag: googleTask.etag,
          google_position: googleTask.position,
        });
    }
  }

  async syncGoogleTaskToFlowState(
    userId: string,
    listId: string,
    googleTask: GoogleTask
  ): Promise<void> {
    if (googleTask.deleted) {
      const { data: deletedMapping } = await this.supabase
        .from("google_task_sync_mappings")
        .select("flowstate_todo_id")
        .eq("google_task_id", googleTask.id)
        .single();

      if (deletedMapping) {
        await this.supabase
          .from("todos")
          .update({ status: "cancelled" })
          .eq("id", deletedMapping.flowstate_todo_id);

        await this.supabase
          .from("google_task_sync_mappings")
          .delete()
          .eq("google_task_id", googleTask.id);

        log.debug("Handled deleted Google Task", {
          googleTaskId: googleTask.id,
          todoId: deletedMapping.flowstate_todo_id,
        });
      }
      return;
    }

    const { data: existingMapping } = await this.supabase
      .from("google_task_sync_mappings")
      .select("*")
      .eq("google_task_id", googleTask.id)
      .single();

    if (existingMapping) {
      await this.updateFlowStateTodoFromGoogle(
        existingMapping.flowstate_todo_id,
        listId,
        googleTask
      );

      await this.supabase
        .from("google_task_sync_mappings")
        .update({
          last_synced_at: new Date().toISOString(),
          google_last_modified: googleTask.updated,
          google_etag: googleTask.etag,
          google_position: googleTask.position,
          sync_status: "synced",
        })
        .eq("google_task_id", googleTask.id);
    } else {
      await this.createFlowStateTodoFromGoogle(userId, listId, googleTask);
    }
  }

  async createFlowStateTodoFromGoogle(
    userId: string,
    listId: string,
    googleTask: GoogleTask
  ): Promise<void> {
    const { mapGoogleStatusToFlowState } = await import("@/types/google-tasks");

    const scope = await this.listService.getListScopeForImport(userId, listId);

    let parentId: string | undefined;
    if (googleTask.parent) {
      const { data: parentMapping } = await this.supabase
        .from("google_task_sync_mappings")
        .select("flowstate_todo_id")
        .eq("google_task_id", googleTask.parent)
        .single();
      parentId = parentMapping?.flowstate_todo_id;
    }

    const dueDate = googleTask.due
      ? googleTask.due.split("T")[0] + "T00:00:00.000Z"
      : null;

    const mappedStatus = mapGoogleStatusToFlowState(googleTask.status);

    const { data: todo, error } = await this.supabase
      .from("todos")
      .insert({
        title: googleTask.title,
        description: googleTask.notes ?? null,
        status: mappedStatus,
        due_date: dueDate,
        completed_at: googleTask.completed ?? null,
        actual_end: googleTask.completed ?? null,
        created_by: userId,
        assigned_to: userId,
        organization_id: scope.organization_id,
        project_id: scope.project_id,
        client_id: scope.client_id,
        parent_id: parentId ?? null,
      })
      .select()
      .single();

    if (error || !todo) {
      throw new Error(`Failed to create todo: ${error?.message}`);
    }

    await this.supabase
      .from("google_task_sync_mappings")
      .insert({
        user_id: userId,
        google_task_list_id: listId,
        google_task_id: googleTask.id,
        flowstate_todo_id: todo.id,
        sync_direction: "bidir",
        last_synced_at: new Date().toISOString(),
        google_last_modified: googleTask.updated,
        flowstate_last_modified: todo.updated_at,
        google_etag: googleTask.etag,
        google_position: googleTask.position,
        sync_status: "synced",
      });

    log.debug("Created FlowState todo from Google Task", {
      todoId: todo.id,
      googleTaskId: googleTask.id,
    });
  }

  async updateFlowStateTodoFromGoogle(
    todoId: string,
    listId: string,
    googleTask: GoogleTask
  ): Promise<void> {
    const { mapGoogleStatusToFlowState } = await import("@/types/google-tasks");

    const { data: mapping } = await this.supabase
      .from("google_task_sync_mappings")
      .select("user_id")
      .eq("flowstate_todo_id", todoId)
      .maybeSingle();

    const scope = mapping?.user_id
      ? await this.listService.getListScopeForImport(mapping.user_id, listId)
      : null;

    const mappedStatus = mapGoogleStatusToFlowState(googleTask.status);

    let parentId: string | null = null;
    if (googleTask.parent) {
      const { data: parentMapping } = await this.supabase
        .from("google_task_sync_mappings")
        .select("flowstate_todo_id")
        .eq("google_task_id", googleTask.parent)
        .single();
      parentId = parentMapping?.flowstate_todo_id ?? null;
    }

    const dueDate = googleTask.due
      ? googleTask.due.split("T")[0] + "T00:00:00.000Z"
      : null;

    const updatePayload: Record<string, unknown> = {
      title: googleTask.title,
      description: googleTask.notes ?? null,
      status: mappedStatus,
      due_date: dueDate,
      parent_id: parentId,
    };

    if (scope) {
      updatePayload.organization_id = scope.organization_id;
      updatePayload.project_id = scope.project_id;
      updatePayload.client_id = scope.client_id;
    }

    if (mappedStatus === "completed" && googleTask.completed) {
      updatePayload.completed_at = googleTask.completed;
      updatePayload.actual_end = googleTask.completed;
    } else if (mappedStatus !== "completed") {
      updatePayload.completed_at = null;
      updatePayload.actual_end = null;
    }

    const { error } = await this.supabase
      .from("todos")
      .update(updatePayload as Record<string, never>)
      .eq("id", todoId);

    if (error) {
      throw new Error(`Failed to update todo: ${error.message}`);
    }

    await this.supabase
      .from("google_task_sync_mappings")
      .update({
        last_synced_at: new Date().toISOString(),
        google_last_modified: googleTask.updated,
        google_etag: googleTask.etag,
        google_position: googleTask.position,
        sync_status: "synced",
      })
      .eq("flowstate_todo_id", todoId);

    log.debug("Updated FlowState todo from Google Task", {
      todoId,
      googleTaskId: googleTask.id,
    });
  }

  async createGoogleTaskFromFlowState(
    listId: string,
    todo: Todo
  ): Promise<GoogleTask> {
    const { mapFlowStateStatusToGoogle } = await import("@/types/google-tasks");

    const { data: mapping } = await this.supabase
      .from("google_task_lists")
      .select("user_id")
      .eq("google_task_list_id", listId)
      .single();

    if (!mapping?.user_id) {
      throw new Error("Google Task list owner not found");
    }

    const due =
      GoogleTasksApiClient.normalizeDueDate(todo.due_date) ?? undefined;
    const googleStatus = mapFlowStateStatusToGoogle(todo.status);

    const createRequest: CreateGoogleTaskRequest = {
      title: todo.title,
      notes: todo.description ?? undefined,
      due,
    };

    if (todo.parent_id) {
      const { data: parentMapping } = await this.supabase
        .from("google_task_sync_mappings")
        .select("google_task_id")
        .eq("flowstate_todo_id", todo.parent_id)
        .maybeSingle();
      if (parentMapping?.google_task_id) {
        createRequest.parent = parentMapping.google_task_id;
      }
    }

    const result = await this.taskService.createTask(
      mapping.user_id,
      listId,
      createRequest
    );

    if (!result.success || !result.data) {
      throw new Error(
        typeof result.error === "string"
          ? result.error
          : "Failed to create Google Task from FlowState todo"
      );
    }

    if (googleStatus === "completed") {
      const updateResult = await this.taskService.updateTask(
        mapping.user_id,
        listId,
        result.data.id,
        {
          status: "completed",
          completed: todo.completed_at ?? new Date().toISOString(),
        }
      );
      if (updateResult.success && updateResult.data) {
        return updateResult.data;
      }
    }

    return result.data;
  }

  async updateGoogleTaskFromFlowState(
    taskId: string,
    todo: Todo
  ): Promise<GoogleTask> {
    const { mapFlowStateStatusToGoogle } = await import("@/types/google-tasks");

    const { data: mapping, error } = await this.supabase
      .from("google_task_sync_mappings")
      .select("user_id, google_task_list_id")
      .eq("google_task_id", taskId)
      .single();

    if (error || !mapping) {
      throw new Error("Google Task sync mapping not found");
    }

    const googleStatus = mapFlowStateStatusToGoogle(todo.status);

    const due =
      GoogleTasksApiClient.normalizeDueDate(todo.due_date) ?? undefined;

    const updatePayload: UpdateGoogleTaskRequest = {
      title: todo.title,
      notes: todo.description ?? undefined,
      due,
      status: googleStatus,
    };

    if (googleStatus === "completed") {
      updatePayload.completed = todo.completed_at ?? new Date().toISOString();
    }

    const result = await this.taskService.updateTask(
      mapping.user_id,
      mapping.google_task_list_id,
      taskId,
      updatePayload
    );

    if (!result.success || !result.data) {
      throw new Error(
        typeof result.error === "string"
          ? result.error
          : "Failed to update Google Task from FlowState todo"
      );
    }

    return result.data;
  }
}
